import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useCharacter } from '@/hooks/useCharacter';
import { useAuthStore } from '@/stores/auth';
import {
  getCrimesByCategory,
  calculateSuccessRate,
  type Crime,
  type CrimeCategory,
} from '@/data/crimes';
import { TEXTURES } from '@/lib/assets';
import { itemImage } from '@/lib/assets';
import { formatNumber } from '@/lib/utils';

type TabId = CrimeCategory;

const TABS: { id: TabId; label: string; available: boolean }[] = [
  { id: 'street', label: 'Street', available: true },
  { id: 'brawl', label: 'Brawl', available: true },
  { id: 'sneak', label: 'Sneak', available: true },
  { id: 'sea', label: 'Sea', available: true },
  { id: 'heist', label: 'Heist', available: true },
];

interface MasteryInfo {
  mastery_level: number;
  mastery_xp: number;
  attempts: number;
  successes: number;
  cooldown_until: string | null;
  refresh_used_this_period: boolean;
}

interface CrimeResult {
  outcome: string;
  gold_gained: number;
  xp_gained: number;
  mastery_xp_gained: number;
  mastery_level_new: number;
  mastery_level_old: number;
  hp_lost: number;
  state_after: string;
  state_until: string | null;
  loot_dropped: string[];
  nerve_remaining: number;
  crime_skill_level_new: number;
  crime_skill_level_old: number;
}

export default function CrimesPage(): JSX.Element {
  const navigate = useNavigate();
  const character = useCharacter();
  const refreshCharacter = useAuthStore((s) => s.refreshCharacter);
  const [activeTab, setActiveTab] = useState<TabId>('street');
  const [mastery, setMastery] = useState<Record<string, MasteryInfo>>({});
  const [attempting, setAttempting] = useState(false);
  const [result, setResult] = useState<CrimeResult | null>(null);
  const [resultCrime, setResultCrime] = useState<Crime | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmCrime, setConfirmCrime] = useState<Crime | null>(null);

  useEffect(() => {
    if (!character) return;
    supabase
      .from('crime_mastery')
      .select(
        'crime_code, mastery_level, mastery_xp, attempts, successes, cooldown_until, refresh_used_this_period',
      )
      .eq('character_id', character.id)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, MasteryInfo> = {};
        for (const row of data) {
          map[row.crime_code as string] = row as unknown as MasteryInfo;
        }
        setMastery(map);
      });
  }, [character, result]);

  // On mount: if character is in jail/hospital with an expired timer, auto-release.
  useEffect(() => {
    if (!character) return;
    if (
      (character.state === 'jail' || character.state === 'hospital') &&
      character.state_until &&
      new Date(character.state_until).getTime() <= Date.now()
    ) {
      void supabase.functions
        .invoke('check-state', { body: { characterId: character.id } })
        .then(() => refreshCharacter());
    }
  }, [character, refreshCharacter]);

  const handleAttempt = useCallback(
    async (crime: Crime) => {
      if (!character || attempting) return;
      setAttempting(true);
      setError(null);
      setResult(null);

      try {
        const { data, error: fnError } = await supabase.functions.invoke('crime-attempt', {
          body: { characterId: character.id, crimeId: crime.id },
        });

        if (fnError) throw fnError;
        if (data?.error) {
          const messages: Record<string, string> = {
            insufficient_nerve: 'Not enough nerve.',
            invalid_state: `You can't commit crimes right now.`,
            rate_limited: 'Slow down. Try again in a moment.',
            forbidden: 'Character not found.',
          };
          setError(messages[data.error as string] ?? 'Crime failed. Try again.');
          return;
        }

        setResult(data as CrimeResult);
        setResultCrime(crime);
        await refreshCharacter();
      } catch {
        setError('Crime failed. Check your connection.');
      } finally {
        setAttempting(false);
      }
    },
    [character, attempting, refreshCharacter],
  );

  if (!character) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-parchment-dark text-sm">Loading…</p>
      </main>
    );
  }

  // Jail/Hospital state screens
  if (character.state === 'jail' || character.state === 'hospital') {
    return (
      <StateScreen
        state={character.state}
        until={character.state_until}
        onBack={() => navigate('/game')}
        characterId={character.id}
      />
    );
  }

  const crimes = getCrimesByCategory(activeTab);

  return (
    <div
      className="min-h-dvh"
      style={{
        backgroundImage: `url("${TEXTURES.wood}")`,
        backgroundSize: '600px',
        backgroundRepeat: 'repeat',
      }}
    >
      <div
        className="min-h-dvh"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(12,34,51,0.70) 0%, rgba(5,15,25,0.88) 70%)',
        }}
      >
        {/* Header */}
        <header className="border-b-2 border-wood-mid/40 bg-sea-deep/90 backdrop-blur">
          <div className="mx-auto max-w-6xl px-3 sm:px-5 py-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate('/game')}
              className="inline-flex items-center gap-1.5 rounded-md border border-parchment/20 bg-sea-dark/60 px-3 py-2 text-xs font-display uppercase tracking-wider text-parchment-dark hover:border-candle hover:text-candle transition-colors min-h-11"
            >
              <span aria-hidden>&larr;</span>
              <span>Back</span>
            </button>
            <div className="text-center">
              <h1 className="font-display text-lg sm:text-xl text-parchment tracking-[0.15em] uppercase">
                Crimes
              </h1>
              <p className="text-[10px] sm:text-xs text-parchment-dark/80">
                Crime Skill{' '}
                <span className="font-mono text-parchment">{character.crime_skill_level}</span>
              </p>
            </div>
            <div className="text-right text-parchment">
              <div className="text-[10px] text-parchment-dark/80 uppercase tracking-wider">
                Nerve
              </div>
              <div className="font-mono text-sm">
                {character.nerve}
                <span className="text-parchment-dark/60"> / {character.nerve_max}</span>
              </div>
            </div>
          </div>

          {/* Category tabs */}
          <div className="mx-auto max-w-6xl px-3 sm:px-5 flex gap-1 overflow-x-auto pb-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                disabled={!tab.available}
                onClick={() => tab.available && setActiveTab(tab.id)}
                className={`shrink-0 rounded-t-md px-4 py-2 text-xs font-display uppercase tracking-wider transition-colors min-h-9 ${
                  activeTab === tab.id && tab.available
                    ? 'bg-parchment/90 text-wood-dark border-t-2 border-x-2 border-wood-mid/40'
                    : tab.available
                      ? 'text-parchment-dark hover:text-candle'
                      : 'text-parchment-dark/40 cursor-not-allowed'
                }`}
              >
                {tab.label}
                {!tab.available && <span className="ml-1 text-[9px] italic normal-case">soon</span>}
              </button>
            ))}
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-3 sm:px-5 py-4 sm:py-6 stage-fade-in">
          {error && (
            <div
              role="alert"
              className="mb-4 rounded-md border-2 border-blood-bright/40 bg-blood-bright/10 px-3 py-2.5 text-sm text-blood shadow-sm"
            >
              {error}
            </div>
          )}

          <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {crimes.map((crime) => {
              const m = mastery[crime.id];
              const mLevel = m?.mastery_level ?? 0;
              const successRate = calculateSuccessRate(crime.baseOutcomes.success, mLevel);
              const locked =
                character.crime_skill_level < crime.crimeSkillRequired ||
                character.level < crime.levelRequired;
              const canAttempt = !locked && character.nerve >= crime.nerveCost && !attempting;

              return (
                <CrimeCard
                  key={crime.id}
                  crime={crime}
                  masteryLevel={mLevel}
                  successRate={successRate}
                  locked={locked}
                  canAttempt={canAttempt}
                  attempting={attempting}
                  cooldownUntil={m?.cooldown_until ?? null}
                  refreshUsed={m?.refresh_used_this_period ?? false}
                  characterMarks={character.marks}
                  onAttempt={() => {
                    if (crime.tier >= 5) {
                      setConfirmCrime(crime);
                    } else {
                      void handleAttempt(crime);
                    }
                  }}
                  onRefresh={async () => {
                    try {
                      const { data } = await supabase.functions.invoke('refresh-cooldown', {
                        body: { characterId: character.id, crimeId: crime.id },
                      });
                      if (data?.ok) await refreshCharacter();
                      else setError(data?.error ?? 'Refresh failed.');
                    } catch {
                      setError('Refresh failed.');
                    }
                  }}
                />
              );
            })}
          </div>
        </main>

        {/* Outcome modal */}
        {result && resultCrime && (
          <OutcomeModal
            result={result}
            crime={resultCrime}
            onClose={() => {
              setResult(null);
              setResultCrime(null);
            }}
          />
        )}

        {confirmCrime && (
          <ConfirmAttemptModal
            crime={confirmCrime}
            masteryLevel={mastery[confirmCrime.id]?.mastery_level ?? 0}
            onConfirm={() => {
              const c = confirmCrime;
              setConfirmCrime(null);
              void handleAttempt(c);
            }}
            onCancel={() => setConfirmCrime(null)}
          />
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Crime Card
// -----------------------------------------------------------------------

function CrimeCard({
  crime,
  masteryLevel,
  successRate,
  locked,
  canAttempt,
  attempting,
  cooldownUntil,
  refreshUsed,
  characterMarks,
  onAttempt,
  onRefresh,
}: {
  crime: Crime;
  masteryLevel: number;
  successRate: number;
  locked: boolean;
  canAttempt: boolean;
  attempting: boolean;
  cooldownUntil: string | null;
  refreshUsed: boolean;
  characterMarks: number;
  onAttempt: () => void;
  onRefresh: () => void;
}): JSX.Element {
  const [now] = useState(() => Date.now());
  const onCooldown = cooldownUntil != null && new Date(cooldownUntil).getTime() > now;
  const refreshCost = crime.refreshCostMarks ?? 0;
  const canRefresh = onCooldown && !refreshUsed && refreshCost > 0 && characterMarks >= refreshCost;
  return (
    <div
      className={`relative overflow-hidden rounded-lg border-2 shadow-md transition-all ${
        locked ? 'border-wood-mid/30 opacity-60' : 'border-wood-mid/70 hover:scale-[1.008]'
      }`}
      style={{
        backgroundImage: `url("${TEXTURES.parchment}")`,
        backgroundSize: 'cover',
      }}
    >
      <div className="absolute inset-0 bg-parchment/80 pointer-events-none" />

      <div className="relative px-4 py-4">
        {/* Tier badge */}
        <div className="absolute top-3 right-3 rounded-full border border-wood-mid/40 bg-parchment-light px-2 py-0.5 text-[10px] font-display uppercase tracking-wider text-wood-mid">
          T{crime.tier}
        </div>

        <h3 className="font-display text-base text-wood-dark pr-10 mb-1">{crime.name}</h3>

        <p className="text-xs text-ink-mid leading-relaxed mb-3 line-clamp-2">{crime.flavor}</p>

        {/* Stats row */}
        <div className="flex items-center gap-3 text-xs text-ink-light mb-3">
          <span>
            <span className="text-sea-light font-mono">{crime.nerveCost}</span> Nerve
          </span>
          <span className="text-wood-mid/40">&middot;</span>
          <span>
            <span className="font-mono text-candle-dark">
              {formatNumber(crime.goldMin)}-{formatNumber(crime.goldMax)}
            </span>{' '}
            gold
          </span>
          <span className="text-wood-mid/40">&middot;</span>
          <span>
            <span className="font-mono">{Math.round(successRate * 100)}</span>%
          </span>
        </div>

        {/* Mastery bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-[10px] text-ink-light mb-0.5">
            <span>Mastery {masteryLevel}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full border border-wood-mid/30 bg-parchment-dark/30">
            <div
              className="h-full rounded-full bg-candle-dark transition-[width] duration-500"
              style={{ width: `${masteryLevel}%` }}
            />
          </div>
        </div>

        {locked ? (
          <div className="text-center text-xs text-ink-light italic py-1">
            {crime.crimeSkillRequired > 1
              ? `Crime Skill ${crime.crimeSkillRequired} required`
              : `Level ${crime.levelRequired} required`}
          </div>
        ) : onCooldown ? (
          <div className="space-y-2">
            <div className="text-center text-xs text-ink-light italic">On cooldown</div>
            {refreshCost > 0 && (
              <button
                type="button"
                disabled={!canRefresh}
                onClick={onRefresh}
                className="w-full inline-flex items-center justify-center gap-1 rounded-md border-2 border-candle/30 bg-parchment-light/50 px-3 py-2 font-display tracking-wider uppercase text-[10px] text-candle-dark transition-colors min-h-9 disabled:opacity-40 disabled:cursor-not-allowed hover:border-candle"
              >
                Refresh &mdash; {refreshCost} Marks
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            disabled={!canAttempt || onCooldown}
            onClick={onAttempt}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-3 py-2 font-display tracking-[0.15em] uppercase text-xs shadow-sm hover:bg-sea-dark hover:border-candle transition-all min-h-11 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
          >
            {attempting ? (
              <span
                aria-hidden
                className="inline-block h-3 w-3 rounded-full border-2 border-candle/40 border-t-candle animate-spin"
              />
            ) : null}
            <span>Attempt</span>
          </button>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Outcome Modal
// -----------------------------------------------------------------------

function OutcomeModal({
  result,
  crime,
  onClose,
}: {
  result: CrimeResult;
  crime: Crime;
  onClose: () => void;
}): JSX.Element {
  const isSuccess = result.outcome === 'success';
  const isUnsuccessful = result.outcome === 'unsuccessful';
  const isFailure = result.outcome === 'failure';
  const isCaptured = result.outcome === 'captured';
  const isEscaped = result.outcome === 'escaped';

  const headerColor = isSuccess
    ? 'text-emerald-600'
    : isUnsuccessful
      ? 'text-wood-mid'
      : 'text-blood-bright';

  const headerText = isSuccess
    ? 'Success'
    : isUnsuccessful
      ? 'Unsuccessful'
      : isEscaped
        ? 'Escaped!'
        : isCaptured
          ? 'Captured'
          : 'Failure';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-6 sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-label="Crime outcome"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative w-full max-w-md rounded-lg border-2 border-wood-mid/70 shadow-[0_8px_40px_rgba(0,0,0,0.7)] stage-fade-in overflow-hidden"
        style={{
          backgroundImage: `url("${TEXTURES.parchment}")`,
          backgroundSize: 'cover',
        }}
      >
        <div className="absolute inset-0 bg-parchment/85 pointer-events-none" />

        <div className="relative px-5 sm:px-6 py-6 text-center">
          <h2 className={`font-display text-3xl mb-1 ${headerColor}`}>{headerText}</h2>
          <p className="text-xs text-ink-light mb-4">{crime.name}</p>

          {isSuccess && (
            <div className="space-y-3 mb-5">
              <p className="text-sm text-ink italic leading-relaxed">{crime.successText}</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 rounded-lg border-2 border-candle/40 bg-candle/10 px-3 py-2 shadow-sm">
                  <img
                    src={itemImage('doubloon')}
                    alt=""
                    aria-hidden
                    className="h-6 w-6 object-contain"
                    width={24}
                    height={24}
                  />
                  <span className="font-mono text-lg text-candle-dark">
                    +{formatNumber(result.gold_gained)}
                  </span>
                </div>
                <div className="rounded-lg border-2 border-sea-light/40 bg-sea-light/10 px-3 py-2 shadow-sm">
                  <span className="font-mono text-lg text-sea-light">+{result.xp_gained} XP</span>
                </div>
              </div>
              {result.mastery_xp_gained > 0 && (
                <p className="text-xs text-ink-mid">
                  +{result.mastery_xp_gained} Mastery XP (Level {result.mastery_level_new})
                </p>
              )}
              {result.crime_skill_level_new > result.crime_skill_level_old && (
                <div className="rounded-md border-2 border-candle/50 bg-candle/15 px-3 py-2 text-sm font-display text-candle-dark">
                  Crime Skill Level Up! &rarr; {result.crime_skill_level_new}
                </div>
              )}
              {result.loot_dropped.length > 0 && (
                <p className="text-xs text-candle-dark italic">
                  Loot found: {result.loot_dropped.join(', ')}
                </p>
              )}
            </div>
          )}

          {isUnsuccessful && (
            <p className="text-sm text-ink-mid mb-5 italic">{crime.unsuccessfulText}</p>
          )}

          {isFailure && (
            <div className="space-y-2 mb-5">
              <p className="text-sm text-ink italic leading-relaxed">{crime.failureText}</p>
              {result.hp_lost > 0 && <p className="text-sm text-blood">-{result.hp_lost} HP</p>}
              {result.state_after === 'hospital' && (
                <p className="text-sm text-blood-bright">Sent to the hospital.</p>
              )}
            </div>
          )}

          {(isCaptured || isEscaped) && (
            <div className="space-y-2 mb-5">
              {isEscaped ? (
                <p className="text-sm text-candle-dark italic">
                  You vanish into the crowd before they can lay hands on you. Your purse is lighter
                  by half, but you&rsquo;re free.
                </p>
              ) : (
                <>
                  <p className="text-sm text-ink italic leading-relaxed">{crime.capturedText}</p>
                  <p className="text-sm text-blood-bright">
                    Sent to {result.state_after}.
                    {result.state_until &&
                      ` Released at ${new Date(result.state_until).toLocaleTimeString()}.`}
                  </p>
                  {result.hp_lost > 0 && <p className="text-sm text-blood">-{result.hp_lost} HP</p>}
                </>
              )}
            </div>
          )}

          <p className="text-xs text-ink-light mb-4">
            Nerve remaining: <span className="font-mono">{result.nerve_remaining}</span>
          </p>

          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-5 py-3 font-display tracking-[0.2em] uppercase text-sm shadow-sm hover:bg-sea-dark hover:border-candle transition-all min-h-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
          >
            {isSuccess || isUnsuccessful || isEscaped ? 'Continue' : 'Understood'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// State Screen (Jail / Hospital)
// -----------------------------------------------------------------------

function StateScreen({
  state,
  until,
  onBack,
  characterId,
}: {
  state: string;
  until: string | null;
  onBack: () => void;
  characterId: string;
}): JSX.Element {
  const [timeLeft, setTimeLeft] = useState('');
  const refreshCharacter = useAuthStore((s) => s.refreshCharacter);
  const [releasing, setReleasing] = useState(false);

  const tryRelease = useCallback(async () => {
    if (releasing) return;
    setReleasing(true);
    try {
      const { data } = await supabase.functions.invoke('check-state', {
        body: { characterId },
      });
      if (data?.state === 'free') {
        await refreshCharacter();
      } else {
        setTimeout(() => setReleasing(false), 5000);
      }
    } catch {
      setTimeout(() => setReleasing(false), 5000);
    }
  }, [characterId, refreshCharacter, releasing]);

  useEffect(() => {
    if (!until) return;
    const target = new Date(until).getTime();

    function update(): void {
      const diff = target - Date.now();
      if (diff <= 0) {
        setTimeLeft('Releasing…');
        void tryRelease();
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
    }

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [until, tryRelease]);

  const isJail = state === 'jail';

  return (
    <div
      className="min-h-dvh flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage: `url("${isJail ? '/art/backgrounds/jail_cell.webp' : '/art/backgrounds/hospital.webp'}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="relative w-full max-w-md rounded-lg border-2 border-wood-mid/70 shadow-[0_8px_32px_rgba(0,0,0,0.6)] overflow-hidden stage-fade-in"
        style={{
          backgroundImage: `url("${TEXTURES.parchment}")`,
          backgroundSize: 'cover',
        }}
      >
        <div className="absolute inset-0 bg-parchment/85 pointer-events-none" />
        <div className="relative px-6 py-8 text-center">
          <h1 className="font-display text-3xl text-wood-dark mb-2">
            {isJail ? "You're in Jail" : "You're in the Hospital"}
          </h1>
          <div className="font-mono text-4xl text-blood-bright mb-4">{timeLeft || '…'}</div>
          <p className="text-sm text-ink-mid mb-6">
            {isJail
              ? "You'll be released when the timer expires. Bond options coming soon."
              : "You'll be released when the timer expires. Medicine coming soon."}
          </p>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center justify-center rounded-md border-2 border-wood-mid/40 bg-parchment-light/60 px-5 py-2.5 font-display text-xs uppercase tracking-wider text-ink-mid hover:border-wood-mid transition-colors min-h-11"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Tier 5+ Confirmation Modal
// -----------------------------------------------------------------------

function ConfirmAttemptModal({
  crime,
  masteryLevel,
  onConfirm,
  onCancel,
}: {
  crime: Crime;
  masteryLevel: number;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const successRate = calculateSuccessRate(crime.baseOutcomes.success, masteryLevel);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-6 sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm crime attempt"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="relative w-full max-w-md rounded-lg border-2 border-wood-mid/70 shadow-[0_8px_40px_rgba(0,0,0,0.7)] stage-fade-in overflow-hidden"
        style={{ backgroundImage: `url("${TEXTURES.parchment}")`, backgroundSize: 'cover' }}
      >
        <div className="absolute inset-0 bg-parchment/85 pointer-events-none" />
        <div className="relative px-5 sm:px-6 py-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl text-wood-dark">{crime.name}</h2>
            <span className="rounded-full border border-wood-mid/40 bg-parchment-light px-2 py-0.5 text-[10px] font-display uppercase tracking-wider text-wood-mid">
              T{crime.tier}
            </span>
          </div>
          <p className="text-xs text-ink-mid italic leading-relaxed mb-4">{crime.flavor}</p>
          <div className="space-y-2 text-sm text-ink mb-4">
            <div className="flex justify-between">
              <span className="text-ink-light">Nerve cost</span>
              <span className="font-mono">{crime.nerveCost}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-light">Success chance</span>
              <span className="font-mono text-emerald-700">{Math.round(successRate * 100)}%</span>
            </div>
            {crime.failureHpLossMax > 0 && (
              <div className="flex justify-between">
                <span className="text-ink-light">HP loss on failure</span>
                <span className="font-mono text-blood">
                  {crime.failureHpLossMin}&ndash;{crime.failureHpLossMax}
                </span>
              </div>
            )}
            {crime.captureDurationMax > 0 && (
              <div className="flex justify-between">
                <span className="text-ink-light">Capture time</span>
                <span className="font-mono text-ink-mid">
                  {crime.captureDurationMin === crime.captureDurationMax
                    ? `${crime.captureDurationMin} min`
                    : `${crime.captureDurationMin}–${crime.captureDurationMax} min`}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded-md border-2 border-wood-mid/40 bg-parchment-light/60 px-3 py-2.5 font-display text-xs uppercase tracking-wider text-ink-mid hover:border-wood-mid transition-colors min-h-11"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 rounded-md bg-blood/80 text-parchment border-2 border-blood-bright/50 px-3 py-2.5 font-display text-xs uppercase tracking-wider shadow-sm hover:bg-blood transition-all min-h-11 focus:outline-none focus-visible:ring-2 focus-visible:ring-blood-bright"
            >
              Attempt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
